type VimeoVolumeChangeEvent = {
  volume: number;
};

type VimeoPlayerOptions = {
  id: string;
  responsive?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  controls?: boolean;
  chapters?: boolean;
  muted?: boolean;
};

type VimeoPlayerInstance = {
  on: (event: string, handler: (...args: any[]) => void) => void;
  off: (event: string) => void;
  destroy: () => void;

  play: () => Promise<void>;
  pause: () => Promise<void>;
  loadVideo: (id: string) => Promise<void>;

  getDuration: () => Promise<number>;
  getCurrentTime: () => Promise<number>;
  setCurrentTime: (seconds: number) => Promise<void>;

  setVolume: (volume: number) => Promise<number>;
};

declare global {
  interface Window {
    Vimeo?: {
      Player: new (
        elementOrId: HTMLElement | string,
        options: VimeoPlayerOptions
      ) => VimeoPlayerInstance;
    };
  }
}

/**
 * Vimeo プレイヤー初期化
 * @param selector data-video を持つコンテナ要素のセレクタ
 */
const vimeoPlayer = (selector: string): void => {
  const video = document.querySelector<HTMLElement>(selector);
  if (!video) return;

  const videoIdsString = video.getAttribute('data-video');
  if (!videoIdsString) return;

  // カンマ区切りの動画IDを配列に整形
  const videoIds = videoIdsString
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

  if (videoIds.length === 0) return;

  // タッチデバイス判定
  const isTouchDevice =
    'ontouchstart' in window || navigator.maxTouchPoints > 0;

  let player: VimeoPlayerInstance | null = null;

  let isPlaying = false;
  let isMuted = true;

  let currentVolume = 0;
  const maxVolume = 0.6;

  let playButton: HTMLButtonElement | null = null;
  let muteButton: HTMLButtonElement | null = null;

  let currentVideoIndex = 0;

  let clickVideoHandler: ((e: MouseEvent) => void) | null = null;
  let keyboardHandler: ((e: KeyboardEvent) => void) | null = null;

  // ローディング表示の切り替え
  const setLoading = (loading: boolean): void => {
    if (loading) {
      video.classList.add('is-loading');
      return;
    }
    setTimeout(() => {
      video.classList.remove('is-loading');
    }, 300);
  };

  const cleanupPlayer = (): void => {
    if (player) {
      // イベントリスナーを解除
      player.off('ready');
      player.off('play');
      player.off('loaded');
      player.off('pause');
      player.off('ended');
      player.off('seeked');
      player.off('progress');
      player.off('volumechange');

      // プレイヤーを破棄
      player.destroy();
      player = null;
    }

    // クリック用イベントリスナーを解除
    if (clickVideoHandler) {
      video.removeEventListener('click', clickVideoHandler);
      clickVideoHandler = null;
    }

    // キーボード用イベントリスナーを解除
    if (keyboardHandler) {
      document.removeEventListener('keydown', keyboardHandler);
      keyboardHandler = null;
    }

    // playボタンを削除
    if (playButton?.parentNode) {
      playButton.parentNode.removeChild(playButton);
    }
    playButton = null;

    // muteボタンを削除
    if (muteButton?.parentNode) {
      muteButton.parentNode.removeChild(muteButton);
    }
    muteButton = null;

    isPlaying = false;
    video.classList.remove('is-playing');
    setLoading(false);
  };

  const getCurrentVideoId = (): string => videoIds[currentVideoIndex];

  const nextVideo = (): string => {
    currentVideoIndex = (currentVideoIndex + 1) % videoIds.length;
    return getCurrentVideoId();
  };

  // 指定秒数だけシーク（前後移動）
  const seekVideo = async (seconds: number): Promise<void> => {
    if (!player) return;

    try {
      const duration = await player.getDuration();
      const currentTime = await player.getCurrentTime();

      const newTime =
        seconds > 0
          ? Math.min(duration, currentTime + seconds)
          : Math.max(0, currentTime + seconds);

      await player.setCurrentTime(newTime);
    } catch {
      // 失敗してもUIを止めない
    }
  };

  const createPlayButton = (): void => {
    // 既存のplayボタンを削除
    if (playButton?.parentNode) {
      playButton.parentNode.removeChild(playButton);
    }

    // 新しいplayボタンを作成
    playButton = document.createElement('button');
    playButton.type = 'button';
    playButton.className = 'yt-video__play';
    playButton.setAttribute('aria-label', 'play');

    playButton.addEventListener('click', (e) => {
      e.stopPropagation(); // 親要素側のクリックイベントが発火しないようにする
      if (!player) return;

      if (isPlaying) {
        void player.pause();
      } else {
        void player.setVolume(isMuted ? 0 : maxVolume).then(() => {
          void player?.play();
        });
      }
    });

    video.appendChild(playButton);
  };

  const setMuted = (muted: boolean): void => {
    if (isMuted === muted) return;
    isMuted = muted;

    // ボタンがまだ無いタイミングでも落ちないようにガード
    if (!muteButton) return;

    if (muted) {
      muteButton.classList.add('is-muted');
      muteButton.setAttribute('aria-label', 'unmute');
    } else {
      muteButton.classList.remove('is-muted');
      muteButton.setAttribute('aria-label', 'mute');
    }
  };

  const createMuteButton = (): void => {
    // 既存のmuteボタンを削除
    if (muteButton?.parentNode) {
      muteButton.parentNode.removeChild(muteButton);
    }

    muteButton = document.createElement('button');
    muteButton.type = 'button';
    muteButton.className = 'yt-video__mute';
    muteButton.setAttribute('aria-label', isMuted ? 'unmute' : 'mute');
    if (isMuted) muteButton.classList.add('is-muted');

    muteButton.addEventListener('click', (e) => {
      e.stopPropagation(); // 親要素側のクリックイベントが発火しないようにする
      if (!player) return;

      setMuted(!isMuted);
      void player.setVolume(isMuted ? 0 : maxVolume);
    });

    video.appendChild(muteButton);
  };

  const createVideoPlayer = (container: HTMLElement, videoId: string): void => {
    if (!window.Vimeo?.Player) return;

    // 既存プレイヤーをクリーンアップ
    cleanupPlayer();

    // ローディングを表示
    setLoading(true);

    player = new window.Vimeo.Player(container, {
      id: videoId,
      responsive: true,
      autoplay: false,
      loop: false,
      controls: false,
      chapters: false,
      muted: isMuted,
    });

    player.on('ready', () => {
      // プレイヤーの初期化が完了したらローディングを解除
      setLoading(false);
    });

    player.on('play', () => {
      isPlaying = true;
      video.classList.add('is-playing');

      // playボタンのラベルを更新
      playButton?.setAttribute('aria-label', 'pause');

      // if (isTouchDevice) void player?.requestFullscreen?.();
    });

    player.on('loaded', () => {
      // 動画が読み込まれたらローディングを解除
      setLoading(false);
    });

    player.on('pause', () => {
      isPlaying = false;
      playButton?.setAttribute('aria-label', 'play');
      video.classList.remove('is-playing');

      // if (isTouchDevice) void player?.exitFullscreen?.();
    });

    player.on('volumechange', (data: VimeoVolumeChangeEvent) => {
      currentVolume = data.volume;
    });

    player.on('ended', () => {
      isPlaying = false;
      video.classList.remove('is-playing');
      playButton?.setAttribute('aria-label', 'play');

      const p = player; // ← ここでスナップショット
      if (!p) return;

      // 次の動画がある場合は次の動画へ
      if (videoIds.length > 1) {
        setLoading(true);

        window.dispatchEvent(
          new CustomEvent('vimeo-ended', {
            detail: {
              nextIndex: (currentVideoIndex + 1) % videoIds.length,
            },
          })
        );

        window.setTimeout(() => {
          // setTimeout 後に player が破棄されている可能性があるので再チェック
          if (player !== p) return;

          const nextVideoId = nextVideo();

          void (async () => {
            try {
              await p.loadVideo(nextVideoId);
              await p.setVolume(isMuted ? 0 : maxVolume);

              // タッチ端末はミュート時のみ自動再生（元コード踏襲）
              if (!isTouchDevice || isMuted) {
                await p.play();
              }
            } catch {
              // noop
            } finally {
              if (player === p) setLoading(false);
            }
          })();
        }, 300);

        return;
      }

      // 1本のみ：同じ動画を再読み込み
      window.setTimeout(() => {
        if (player !== p) return;

        setLoading(true);

        void (async () => {
          try {
            await p.loadVideo(videoId);
            await p.setVolume(isMuted ? 0 : maxVolume);
          } catch {
            // noop
          } finally {
            if (player === p) setLoading(false);
          }
        })();
      }, 300);
    });

    player.on('seeked', () => {});
    player.on('progress', () => {});

    // クリックでコントロール表示状態にする
    clickVideoHandler = () => {
      video.classList.add('is-controlable');
      window.setTimeout(() => {
        video.classList.remove('is-controlable');
      }, 3000);
    };
    video.addEventListener('click', clickVideoHandler);

    // キーボード操作（再生中または要素フォーカス中のみ）
    keyboardHandler = (e: KeyboardEvent) => {
      if (!player) return;

      // 要素がフォーカスされている、または再生中のときだけ左右キー操作を有効にする
      if (video.contains(document.activeElement) || isPlaying) {
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            void seekVideo(-3); // 3秒戻す
            break;
          case 'ArrowRight':
            e.preventDefault();
            void seekVideo(3); // 3秒進める
            break;
        }
      }
    };
    document.addEventListener('keydown', keyboardHandler);

    // UI生成
    createPlayButton();
    createMuteButton();
  };

  // コンテナ（Vimeo.Player を差し込む要素）を取得
  const container = document.getElementById('yt-video');
  if (!container) return;

  // プレイヤーを初期化（最初の動画を読み込む）
  createVideoPlayer(container, getCurrentVideoId());
};

export default vimeoPlayer;

document.addEventListener('DOMContentLoaded', () => {
  vimeoPlayer('#yt-video');
});
